// Who's signed in, their plan and today's usage. Sign-in is passwordless: an
// emailed 6-digit code or one-tap link (POST /auth/start → /auth/verify).

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { post, getSession, setSession, onSignedOut, onLimit } from "./api";
import { hydrate, signOutState, flush } from "./state";
import { track } from "./analytics";
import type { Usage, User } from "./types";

interface AccountResponse {
  user: User;
  tier: "free" | "pro";
  usage: Usage;
  limits: { voiceMaxSeconds: number };
  state: unknown;
  stateVersion: number;
}

interface Auth {
  status: "loading" | "signedOut" | "signedIn";
  user: User | null;
  tier: "free" | "pro";
  usage: Usage | null;
  voiceMaxSeconds: number;
  /** Re-read plan + usage (after a call, a checkout, …). */
  refresh: () => Promise<void>;
  startSignIn: (email: string) => Promise<void>;
  verify: (args: { email: string; code?: string; token?: string }) => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const Ctx = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Auth["status"]>(getSession() ? "loading" : "signedOut");
  const [acct, setAcct] = useState<AccountResponse | null>(null);

  const apply = useCallback((a: AccountResponse, hydrateState: boolean) => {
    setAcct(a);
    if (hydrateState) hydrate(a.user.id, a.state, a.stateVersion);
    setStatus("signedIn");
  }, []);

  const refresh = useCallback(async () => {
    if (!getSession()) return;
    const a = await post<AccountResponse>("/account");
    setAcct(a);
  }, []);

  // Boot: resume the stored session.
  useEffect(() => {
    if (!getSession()) return;
    post<AccountResponse>("/account").then(
      (a) => apply(a, true),
      (e) => {
        // Offline with a session: keep going on local state; 401 already cleared it.
        if (getSession() && e?.status === 0) setStatus("signedIn");
        else setStatus("signedOut");
      }
    );
  }, [apply]);

  useEffect(
    () =>
      onSignedOut(() => {
        signOutState();
        setAcct(null);
        setStatus("signedOut");
      }),
    []
  );
  // A limit hit means usage changed: refresh the counters.
  useEffect(() => onLimit(() => void refresh().catch(() => {})), [refresh]);

  const startSignIn = useCallback(async (email: string) => {
    await post("/auth/start", { email });
    track("signin_start");
  }, []);

  const verify = useCallback(
    async (args: { email: string; code?: string; token?: string }) => {
      const r = await post<{ session: string; user: User; state: unknown; stateVersion: number }>("/auth/verify", args);
      setSession(r.session);
      const a = await post<AccountResponse>("/account");
      apply(a, true);
      track("signin_complete", { via: args.token ? "link" : "code" });
    },
    [apply]
  );

  const signOut = useCallback(async () => {
    await flush().catch(() => {});
    await post("/auth/logout").catch(() => {});
    setSession(null);
    signOutState();
    setAcct(null);
    setStatus("signedOut");
  }, []);

  const deleteAccount = useCallback(async () => {
    await post("/account/delete");
    setSession(null);
    signOutState();
    setAcct(null);
    setStatus("signedOut");
  }, []);

  const value = useMemo<Auth>(
    () => ({
      status,
      user: acct?.user ?? null,
      tier: acct?.tier ?? "free",
      usage: acct?.usage ?? null,
      voiceMaxSeconds: acct?.limits.voiceMaxSeconds ?? 300,
      refresh,
      startSignIn,
      verify,
      signOut,
      deleteAccount,
    }),
    [status, acct, refresh, startSignIn, verify, signOut, deleteAccount]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): Auth {
  const a = useContext(Ctx);
  if (!a) throw new Error("useAuth outside AuthProvider");
  return a;
}
